import { Button, Dialog, Typography } from '@neo4j-ndl/react';
import { MODAL_TYPE, useModal, useModalClose } from '@nx/state';
import type { DbmsInfo, SelectedFile } from '@nx/state/desktop';
import {
  useCreateDatabaseMutation,
  useListDatabasesQuery,
  useLoadDatabaseMutation,
  useOpenDialogMutation,
  useRestoreBackupMutation,
} from '@nx/state/desktop';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { useEffect, useState } from 'react';

import { validateDatabaseName } from '../database/db-name-validation';
import { ErrorBanner } from '../error-banner';
import { useToggleInstanceRunStatus } from '../hooks/use-toggle-instance-run-status';

export const useImportDumpFilePicker = (instance: DbmsInfo) => {
  const [open] = useOpenDialogMutation();
  const importDatabaseDumpModal = useModal(MODAL_TYPE.DBMS_DB_IMPORT_DUMP);

  return async () => {
    const res = await open({
      title: 'Select backup',
      properties: ['openFile'],
      message: 'Select a file',
      defaultPath: [instance.rootPath ?? '', 'data', 'dumps'],
    });

    if (isNotNullish(res.data) && res.data.fileName !== '') {
      importDatabaseDumpModal.open({
        instance,
        file: res.data,
        rootPath: undefined,
      });
    }
  };
};

const validateFile = (file: SelectedFile): string | undefined => {
  if (!['.dump', '.backup', '.tar'].some((type) => file.filePath.endsWith(type))) {
    return 'Selected file is not valid. Make sure your selected file is one of the supported types';
  }
  return undefined;
};

interface Props {
  instance: DbmsInfo;
  file: SelectedFile;
}

export const ImportDatabaseDumpModal = ({ instance, file }: Props) => {
  const closeModal = useModalClose();
  const [errorMessage, setErrorMessage] = useState<{ message: string; raw?: boolean } | null>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [fileHasError, setFileHasError] = useState(false);
  const openFilePicker = useImportDumpFilePicker(instance);
  const [dbName, setDbName] = useState<string>('');

  const { data: databases = [] } = useListDatabasesQuery(instance.id);
  const [isOverwriting, setIsOverwriting] = useState<boolean>(false);

  const [createDatabase] = useCreateDatabaseMutation();
  const [loadDump] = useLoadDatabaseMutation();
  const [restoreBackup] = useRestoreBackupMutation();
  const { toggleRunStatus } = useToggleInstanceRunStatus(instance);

  useEffect(() => {
    if (!isProcessing) {
      setIsOverwriting(isNotNullish(databases) && databases.some((db) => db.databaseName === dbName));
    }
  }, [databases, dbName, isProcessing]);

  useEffect(() => {
    let validationError = validateFile(file);
    let newDbName = '';
    if (isNullish(validationError)) {
      newDbName = file.fileName.split('.').slice(0, -1).join('.').toLowerCase() || '';
      validationError = validateDatabaseName(newDbName);
      if (isNotNullish(validationError)) {
        validationError += '. Please rename the file or select a different file';
      }
    }

    if (isNullish(validationError)) {
      setDbName(newDbName);
      setFileHasError(false);
      setErrorMessage(null);
    } else {
      setDbName('');
      setFileHasError(true);
      setErrorMessage({ message: validationError });
    }
  }, [file]);

  const handleSubmit = async () => {
    setIsProcessing(true);

    try {
      if (file.filePath.endsWith('.backup')) {
        await restoreBackup({
          dbmsId: instance.id,
          database: dbName,
          from: file.filePath,
          force: isOverwriting,
        }).unwrap();
      } else {
        await loadDump({ dbmsId: instance.id, database: dbName, from: file.dirName, force: isOverwriting }).unwrap();
      }
      await toggleRunStatus();
      if (!isOverwriting) {
        await createDatabase({ dbmsId: instance.id, database: dbName }).unwrap();
      }
      closeModal();
    } catch (e) {
      setErrorMessage({ message: JSON.stringify(e, null, 4), raw: true });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog
      isOpen
      onClose={closeModal}
      size="unset"
      modalProps={{ className: 'overflow-y-visible max-w-[608px]' }}
      type={isOverwriting ? 'warning' : undefined}
    >
      <div className={isOverwriting ? '[&>*]:max-w-[380px]' : ''}>
        <Dialog.Header>{isOverwriting ? `Database already exists` : 'Load database from file'}</Dialog.Header>
        <Dialog.Description className="flex flex-col gap-4">
          {!isOverwriting && <p>Supports .dump, .backup and .tar</p>}
          {isNotNullish(errorMessage) && <ErrorBanner raw={errorMessage.raw} error={errorMessage.message} />}
          {isOverwriting ? (
            <>
              <p>
                Database with the name <span className="break-words font-bold">{dbName}</span> will be overwritten.
              </p>
              <Typography variant="body-small">
                You can choose another file or rename this file in your file system.
              </Typography>
            </>
          ) : null}
          {!isOverwriting && dbName !== '' ? (
            <p>
              Database with the name <span className="break-words font-bold">{dbName}</span> will be created. To change
              its name you can rename this file in your file system.
            </p>
          ) : null}
        </Dialog.Description>
        <Dialog.Content className="overflow-auto">
          <p className="text-palette-neutral-text-weak mb-1">File path</p>
          <p className="break-words font-bold">{file.filePath}</p>
        </Dialog.Content>
        <Dialog.Actions>
          <Button
            className="mr-auto"
            color="neutral"
            fill="outlined"
            onClick={() => {
              void openFilePicker();
            }}
            isDisabled={isProcessing}
          >
            Browse
          </Button>
          <Button onClick={closeModal} color="neutral" fill="outlined" isDisabled={isProcessing}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleSubmit();
            }}
            isDisabled={fileHasError}
            isLoading={isProcessing}
          >
            {isOverwriting ? 'Overwrite' : 'Create'}
          </Button>
        </Dialog.Actions>
      </div>
    </Dialog>
  );
};
